import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import TopMenu from '../../../TopMenu';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  'https://localhost:7038';

const TEMPLATE_COLORS = {
  'sidebar-teal': '#0d9488',
  'executive-dark': '#1e293b',
  'minimal-clean': '#2563eb',
  'navy-pro': '#1e3a5f',
  'soft-column': '#64748b',
  'sidebar-slate': '#6366f1',
  'minimal': '#3b82f6',
  'modern': '#0ea5e9',
};

const NAV_SECTIONS = [
  { id: 'personal',       title: 'Header',       icon: '👤' },
  { id: 'experience',     title: 'Experience',   icon: '💼' },
  { id: 'education',      title: 'Education',    icon: '🎓' },
  { id: 'skills',         title: 'Skills',       icon: '🛠️' },
  { id: 'objective',      title: 'Summary',      icon: '📝' },
  { id: 'other',          title: 'Other sections', icon: '➕' },
];

const SECTION_FLOW = [
  'personal','objective','education','experience',
  'projects','skills','positions','achievements','certifications','other',
];

const HERO_COPY = {
  personal:       { title: 'How do you want recruiters to contact you?',       text: 'Add the core contact details that should appear at the top of your resume.' },
  objective:      { title: 'Add your summary',                                  text: 'Write a short summary that highlights your strengths and direction.' },
  education:      { title: 'Add your education',                                text: 'List your degrees, school, city, and graduation details.' },
  experience:     { title: 'Add your experience',                               text: 'Start with your most recent job first and add impactful achievements.' },
  projects:       { title: 'Add your projects',                                 text: 'Include the work that shows off your skills and outcomes.' },
  skills:         { title: 'Add your skills',                                   text: 'Capture the tools, languages, and strengths recruiters should notice.' },
  positions:      { title: 'Add your positions',                                text: 'Highlight leadership, responsibility, and extra involvement.' },
  achievements:   { title: 'Add your achievements',                             text: 'Use awards, wins, and proof points that help you stand out.' },
  certifications: { title: 'Add your certifications',                           text: 'List the certificates that support your profile.' },
  other:          { title: 'Add other sections',                                text: 'Use this space for languages, hobbies, or any custom section.' },
};

const emptyForm = {
  personal:       { fullName: '', email: '', phone: '', linkedin: '', github: '', location: '', profileImageBase64: '' },
  objective:      '',
  education:      [{ degree: '', institution: '', location: '', percentage: '', duration: '' }],
  experience:     [{ title: '', company: '', location: '', duration: '', description: '' }],
  projects:       [{ name: '', techStack: '', description: '' }],
  positions:      [{ title: '', organization: '', duration: '' }],
  skills:         { technical: [] },
  achievements:   [],
  certifications: [{ name: '', issuer: '', date: '' }],
  other:          [],
};

// ─── Merge prefill data from any API shape ────────────────────────────────────
function mergeWithPrefill(prefill) {
  if (!prefill) return { ...emptyForm };
  const root = prefill.data ?? prefill.formData ?? prefill.parsedData ?? prefill;
  const p = root.personal ?? {};
  const technical = Array.isArray(root.skills) ? root.skills : (root.skills?.technical ?? []);
  return {
    personal: {
      fullName:          p.fullName        ?? '',
      email:             p.email           ?? '',
      phone:             p.phone           ?? '',
      linkedin:          p.linkedIn ?? p.linkedin ?? '',
      github:            p.gitHub  ?? p.github   ?? '',
      location:          p.location        ?? '',
      profileImageBase64: p.profileImageBase64 ?? '',
    },
    objective:      root.objective ?? root.summary ?? '',
    education:      root.education?.length
      ? root.education.map(e => ({
          degree:      e.degree      ?? e.qualification ?? '',
          institution: e.institution ?? e.school        ?? '',
          location:    e.location    ?? '',
          percentage:  e.percentage  ?? e.cgpa ?? e.grade ?? '',
          duration:    e.duration    ?? e.year ?? '',
        }))
      : emptyForm.education,
    experience: root.experience?.length
      ? root.experience.map(e => ({
          title:       e.title    ?? e.role ?? e.position ?? '',
          company:     e.company  ?? e.employer ?? e.organization ?? '',
          location:    e.location ?? '',
          duration:    e.duration ?? e.period ?? '',
          description: e.description ?? e.summary ?? '',
        }))
      : emptyForm.experience,
    projects: root.projects?.length
      ? root.projects.map(p => ({
          name:      p.name  ?? p.title ?? '',
          techStack: p.techStack ?? p.technologies ?? p.tech ?? '',
          description: p.description ?? '',
        }))
      : emptyForm.projects,
    positions:      root.positions?.length ? root.positions : emptyForm.positions,
    skills:         { technical },
    achievements:   Array.isArray(root.achievements) ? root.achievements : [],
    certifications: root.certifications?.length
      ? root.certifications.map(c => ({
          name:   c.name   ?? c.title    ?? '',
          issuer: c.issuer ?? c.issuedBy ?? '',
          date:   c.date   ?? c.issuedDate ?? '',
        }))
      : emptyForm.certifications,
    other: root.other ?? [],
  };
}

// ─── toApiShape — maps frontend form → backend DTO ───────────────────────────
function toApiShape(f) {
  return {
    personal: {
      fullName:           f.personal.fullName,
      email:              f.personal.email,
      phone:              f.personal.phone,
      linkedIn:           f.personal.linkedin,
      gitHub:             f.personal.github,
      location:           f.personal.location,
      profileImageBase64: f.personal.profileImageBase64 || null,
    },
    objective:      f.objective,
    education:      f.education,
    experience:     f.experience,
    projects:       f.projects,
    positions:      f.positions,
    skills:         f.skills,
    achievements:   f.achievements,
    certifications: f.certifications,
    other:          f.other,
  };
}

// ─── Completion % calculator ──────────────────────────────────────────────────
function calcCompletion(f) {
  let score = 0;
  if (f.personal?.fullName)        score += 20;
  if (f.objective)                 score += 10;
  if (f.education?.length)         score += 15;
  if (f.experience?.length)        score += 20;
  if (f.skills?.technical?.length) score += 15;
  if (f.projects?.length)          score += 10;
  if (f.certifications?.length)    score += 10;
  return Math.min(score, 100);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const Field = ({ label, value, onChange, type = 'text', placeholder = '', rows = 3 }) => (
  <div className="re-field">
    <label className="re-label">{label}</label>
    {type === 'textarea'
      ? <textarea className="re-input" rows={rows} value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} />
      : <input    className="re-input" type={type} value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} />
    }
  </div>
);

const Row = ({ children }) => <div className="re-row">{children}</div>;

const Section = ({ id, title, expanded, onToggle, children, accent, visible = true, onNext, onPrev, nextLabel = 'Next', prevLabel = 'Back' }) => {
  if (!visible) return null;
  return (
    <div className="re-section">
      <button className="re-section-head" onClick={() => onToggle(id)} style={expanded ? { borderLeft: `3px solid ${accent}` } : {}}>
        <span className="re-section-title">{title}</span>
        <span className="re-section-chevron">{expanded ? '▲' : '▼'}</span>
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div className="re-section-body"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28 }}
          >
            {children}
            {(onPrev || onNext) && (
              <div className="re-step-nav">
                <button className="re-step-back" type="button" onClick={onPrev} disabled={!onPrev}>{prevLabel}</button>
                <button className="re-step-next" type="button" onClick={onNext} disabled={!onNext}>{nextLabel}</button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Live Preview — renders actual backend template as PDF iframe ─────────────
const LivePreview = ({ data, resumeId, token }) => {
  const [previewUrl, setPreviewUrl]   = useState(null);
  const [loading, setLoading]         = useState(false);
  const debounceRef                   = useRef(null);
  const prevUrlRef                    = useRef(null);

  useEffect(() => {
    if (!resumeId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/resume-builder/preview`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            resumeId,
            format: 'pdf',
            data:   toApiShape(data),
          }),
        });
        if (!res.ok) return;
        const blob = await res.blob();
        if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
        const url = URL.createObjectURL(blob);
        prevUrlRef.current = url;
        setPreviewUrl(url);
      } catch (e) {
        console.error('Preview failed', e);
      } finally {
        setLoading(false);
      }
    }, 2000); // debounce 2s after last keystroke

    return () => clearTimeout(debounceRef.current);
  }, [data, resumeId, token]);

  // Cleanup blob URL on unmount
  useEffect(() => () => { if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current); }, []);

  if (!resumeId) return <div className="re-prev-empty">Fill in your details to see a live preview</div>;

  return (
    <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {loading && (
        <div style={{
          position: 'absolute', top: 8, right: 8, zIndex: 10,
          background: 'rgba(0,0,0,0.65)', color: '#fff',
          fontSize: 11, padding: '3px 12px', borderRadius: 999,
        }}>
          Updating preview...
        </div>
      )}
      {previewUrl
        ? <iframe src={previewUrl} title="Resume Preview"
            style={{ width: '100%', flex: 1, border: 'none', borderRadius: 8, minHeight: 500 }} />
        : <div className="re-prev-empty">
            {loading ? 'Generating preview...' : 'Start filling your details to see preview'}
          </div>
      }
    </div>
  );
};

// ─── Main Editor ──────────────────────────────────────────────────────────────
const ResumeEditor = ({
  theme, toggleTheme,
  templateId, styleConfigJson,
  experience, studying, industry,
  resumeId: initialResumeId,
  prefillData, atsScore = 0,
  onBack,
}) => {
  const cfg    = styleConfigJson ? (() => { try { return JSON.parse(styleConfigJson); } catch { return {}; } })() : {};
  const accent = cfg.accentColor ? `#${cfg.accentColor.replace('#', '')}` : (TEMPLATE_COLORS[templateId] || '#0ea5e9');
  const token  = localStorage.getItem('token');

  const [form, setForm]               = useState(() => mergeWithPrefill(prefillData));
  const [open, setOpen]               = useState({
    personal: true, objective: true, education: true,
    experience: experience === 'experienced',
    projects: true, positions: false, skills: true,
    achievements: false, certifications: false, other: false,
  });
  const [skillInput, setSkillInput]   = useState('');
  const [saveStatus, setSaveStatus]   = useState('');
  const [isSaving, setIsSaving]       = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [activeSection, setActiveSection] = useState('personal');
  const [resumeId]                    = useState(initialResumeId ?? null);

  const profileRef  = useRef(null);
  const autoSaveRef = useRef(null);
  const sectionRefs = useRef({});

  // Re-merge if prefillData changes (e.g. parse upload)
  useEffect(() => {
    if (prefillData) setForm(mergeWithPrefill(prefillData));
  }, [prefillData]);

  // ── Save to API ─────────────────────────────────────────────────────────────
  const saveToApi = useCallback(async (formData) => {
    if (!resumeId) return;
    try {
      setIsSaving(true);
      setSaveStatus('saving');
      const res = await fetch(`${API_BASE_URL}/api/resume-builder/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          resumeId,
          completionPercent: calcCompletion(formData),
          data: toApiShape(formData),           // ← correct key: "data"
        }),
      });
      setSaveStatus(res.ok ? 'saved' : 'error');
    } catch {
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveStatus(''), 3000);
    }
  }, [resumeId, token]);

  // Auto-save every 30s
  useEffect(() => {
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(() => saveToApi(form), 30000);
    return () => clearTimeout(autoSaveRef.current);
  }, [form, saveToApi]);

  // ── Download ────────────────────────────────────────────────────────────────
  const download = async (format) => {
    if (!resumeId) { alert('Resume not initialised yet.'); return; }
    setDownloading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/resume-builder/download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          resumeId,
          format,                               // "pdf" or "ats-docx"
          data: toApiShape(form),               // ← correct endpoint + body
        }),
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const ext  = format === 'ats-docx' ? '.docx' : '.pdf';
      const name = (form.personal.fullName || 'Resume').replace(/\s+/g, '_') + '_Resume' + ext;
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  // ── Form helpers ────────────────────────────────────────────────────────────
  const toggle   = id => setOpen(p => ({ ...p, [id]: !p[id] }));
  const setP     = (k, v) => setForm(p => ({ ...p, personal: { ...p.personal, [k]: v } }));
  const setArr   = (sec, idx, k, v) => setForm(p => ({ ...p, [sec]: p[sec].map((item, i) => i === idx ? (k ? { ...item, [k]: v } : v) : item) }));
  const addArr   = (sec, tpl) => setForm(p => ({ ...p, [sec]: [...p[sec], tpl] }));
  const delArr   = (sec, idx) => setForm(p => ({ ...p, [sec]: p[sec].filter((_, i) => i !== idx) }));
  const addSkill = () => { const s = skillInput.trim(); if (!s) return; setForm(p => ({ ...p, skills: { technical: [...p.skills.technical, s] } })); setSkillInput(''); };
  const delSkill = i => setForm(p => ({ ...p, skills: { technical: p.skills.technical.filter((_, j) => j !== i) } }));
  const handleProfileImg = e => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setP('profileImageBase64', ev.target.result);
    reader.readAsDataURL(file);
  };
  const focusSection = id => {
    setActiveSection(id);
    setOpen(prev => ({ ...prev, [id]: true }));
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const goToSection  = id => { if (!id) return; focusSection(id); };
  const goNextSection = () => { const idx = SECTION_FLOW.indexOf(activeSection); goToSection(SECTION_FLOW[idx + 1] || SECTION_FLOW[idx]); };
  const goPrevSection = () => { const idx = SECTION_FLOW.indexOf(activeSection); goToSection(SECTION_FLOW[idx - 1] || SECTION_FLOW[idx]); };

  return (
    <div className={`rb-shell ${theme} re-editor-shell`}>
      <TopMenu theme={theme} toggleTheme={toggleTheme} />
      <div className="re-layout">

        {/* ── Sidebar ── */}
        <aside className="re-sidebar">
          <div className="re-sidebar-brand">
            <div className="re-sidebar-logo">LB</div>
            <div className="re-sidebar-brand-text">
              <strong>Resume Builder</strong>
              <span>{theme === 'dark' ? 'Dark mode' : 'Light mode'}</span>
            </div>
          </div>
          <nav className="re-sidebar-nav">
            {NAV_SECTIONS.map(item => (
              <button key={item.id} type="button"
                className={`re-sidebar-link${activeSection === item.id ? ' active' : ''}`}
                onClick={() => focusSection(item.id)}>
                <span className="re-sidebar-icon">{item.icon}</span>
                <span>{item.title}</span>
              </button>
            ))}
          </nav>
          <div className="re-sidebar-footer">
            <a href="/terms">Terms</a>
            <a href="/privacy">Privacy policy</a>
            <a href="/contact">Contact us</a>
          </div>
        </aside>

        {/* ── Left form ── */}
        <div className="re-form-col">
          <div className="re-form-topbar">
            <button className="re-btn-ghost re-back" onClick={onBack}>← Back</button>
            <div className="re-topbar-info">
              <h2>Build Your Resume</h2>
              <span className="re-topbar-meta">
                <b style={{ color: accent }}>{templateId}</b> · {experience}
                {atsScore > 0 && <span style={{ marginLeft: 8, color: accent }}>Last ATS {atsScore}</span>}
              </span>
            </div>
            <div className="re-topbar-actions">
              {saveStatus === 'saving' && <span style={{ fontSize: 12, color: '#6E6B80' }}>Saving…</span>}
              {saveStatus === 'saved'  && <span style={{ fontSize: 12, color: accent }}>Saved ✓</span>}
              {saveStatus === 'error'  && <span style={{ fontSize: 12, color: '#EF4444' }}>Save failed</span>}
              <button className="re-btn-ghost" onClick={() => saveToApi(form)} disabled={isSaving}>
                {isSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>

          <div className="re-form-scroll">
            {/* Hero card */}
            <div className="re-hero-card">
              <div className="re-hero-copy">
                <h1>{HERO_COPY[activeSection]?.title || HERO_COPY.personal.title}</h1>
                <p>{HERO_COPY[activeSection]?.text  || HERO_COPY.personal.text}</p>
              </div>
              <button className="re-hero-cta" type="button" onClick={() => focusSection('personal')}>Header</button>
            </div>

            {/* ── Personal ── */}
            <div ref={el => sectionRefs.current.personal = el} className="re-section-anchor" />
            <Section id="personal" title="Header Details" visible={activeSection === 'personal'}
              onNext={goNextSection} onPrev={undefined} nextLabel="Next"
              expanded={open.personal} onToggle={id => { setActiveSection(id); toggle(id); }} accent={accent}>
              <div className="re-field" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div onClick={() => profileRef.current?.click()}
                  style={{ width: 52, height: 52, borderRadius: '50%', border: `2px dashed ${accent}`, cursor: 'pointer', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.04)', flexShrink: 0 }}>
                  {form.personal.profileImageBase64
                    ? <img src={form.personal.profileImageBase64} alt="profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <span style={{ fontSize: 18 }}>📷</span>}
                </div>
                <input ref={profileRef} type="file" accept="image/*" onChange={handleProfileImg} style={{ display: 'none' }} />
                <div style={{ fontSize: 11, color: '#6E6B80' }}>Click to upload photo<br /><span style={{ fontSize: 10 }}>Optional · PNG, JPG</span></div>
              </div>
              <Field label="Full Name"  value={form.personal.fullName}  placeholder="e.g. John Smith"          onChange={v => setP('fullName', v)} />
              <Row>
                <Field label="Email"   type="email" value={form.personal.email}   placeholder="you@example.com"        onChange={v => setP('email', v)} />
                <Field label="Phone"   type="tel"   value={form.personal.phone}   placeholder="+91 XXXXX XXXXX"         onChange={v => setP('phone', v)} />
              </Row>
              <Row>
                <Field label="LinkedIn" value={form.personal.linkedin} placeholder="linkedin.com/in/you"       onChange={v => setP('linkedin', v)} />
                <Field label="GitHub"   value={form.personal.github}   placeholder="github.com/username"       onChange={v => setP('github', v)} />
              </Row>
              <Field label="Location" value={form.personal.location} placeholder="City, State"                 onChange={v => setP('location', v)} />
            </Section>

            {/* ── Objective ── */}
            <div ref={el => sectionRefs.current.objective = el} className="re-section-anchor" />
            <Section id="objective" title="Summary" visible={activeSection === 'objective'}
              onNext={goNextSection} onPrev={goPrevSection} nextLabel="Next"
              expanded={open.objective} onToggle={id => { setActiveSection(id); toggle(id); }} accent={accent}>
              <Field type="textarea" rows={4} value={form.objective}
                placeholder="Write a brief career objective or professional summary"
                onChange={v => setForm(p => ({ ...p, objective: v }))} />
            </Section>

            {/* ── Education ── */}
            <div ref={el => sectionRefs.current.education = el} className="re-section-anchor" />
            <Section id="education" title="Education" visible={activeSection === 'education'}
              onNext={goNextSection} onPrev={goPrevSection} nextLabel="Next"
              expanded={open.education} onToggle={id => { setActiveSection(id); toggle(id); }} accent={accent}>
              {form.education.map((edu, i) => (
                <div key={i} className="re-array-item">
                  <div className="re-array-head">
                    <span>Education {i + 1}</span>
                    {form.education.length > 1 && <button className="re-del-btn" onClick={() => delArr('education', i)}>Remove</button>}
                  </div>
                  <Field label="Degree"      value={edu.degree}      placeholder="B.Tech in Computer Science" onChange={v => setArr('education', i, 'degree', v)} />
                  <Field label="Institution" value={edu.institution}  placeholder="University Name"            onChange={v => setArr('education', i, 'institution', v)} />
                  <Row>
                    <Field label="Location" value={edu.location}   placeholder="City"      onChange={v => setArr('education', i, 'location', v)} />
                    <Field label="CGPA"     value={edu.percentage}  placeholder="8.5 CGPA" onChange={v => setArr('education', i, 'percentage', v)} />
                  </Row>
                  <Field label="Duration" value={edu.duration} placeholder="Sep 2022 – Present" onChange={v => setArr('education', i, 'duration', v)} />
                </div>
              ))}
              <button className="re-add-btn" style={{ color: accent }}
                onClick={() => addArr('education', { degree: '', institution: '', location: '', percentage: '', duration: '' })}>
                + Add Education
              </button>
            </Section>

            {/* ── Experience ── */}
            <div ref={el => sectionRefs.current.experience = el} className="re-section-anchor" />
            <Section id="experience" title="Experience" visible={activeSection === 'experience'}
              onNext={goNextSection} onPrev={goPrevSection} nextLabel="Next"
              expanded={open.experience} onToggle={id => { setActiveSection(id); toggle(id); }} accent={accent}>
              {form.experience.map((exp, i) => (
                <div key={i} className="re-array-item">
                  <div className="re-array-head">
                    <span>Experience {i + 1}</span>
                    {form.experience.length > 1 && <button className="re-del-btn" onClick={() => delArr('experience', i)}>Remove</button>}
                  </div>
                  <Field label="Job Title" value={exp.title}    placeholder="Software Engineer" onChange={v => setArr('experience', i, 'title', v)} />
                  <Row>
                    <Field label="Company"  value={exp.company}   placeholder="Company Name"    onChange={v => setArr('experience', i, 'company', v)} />
                    <Field label="Location" value={exp.location}  placeholder="City / Remote"   onChange={v => setArr('experience', i, 'location', v)} />
                  </Row>
                  <Field label="Duration"    value={exp.duration}    placeholder="Jan 2023 – Present"                    onChange={v => setArr('experience', i, 'duration', v)} />
                  <Field type="textarea" label="Description" rows={3} value={exp.description} placeholder="Key responsibilities and achievements" onChange={v => setArr('experience', i, 'description', v)} />
                </div>
              ))}
              <button className="re-add-btn" style={{ color: accent }}
                onClick={() => addArr('experience', { title: '', company: '', location: '', duration: '', description: '' })}>
                + Add Experience
              </button>
            </Section>

            {/* ── Projects ── */}
            <Section id="projects" title="Projects" visible={activeSection === 'projects'}
              onNext={goNextSection} onPrev={goPrevSection} nextLabel="Next"
              expanded={open.projects} onToggle={id => { setActiveSection(id); toggle(id); }} accent={accent}>
              {form.projects.map((proj, i) => (
                <div key={i} className="re-array-item">
                  <div className="re-array-head">
                    <span>Project {i + 1}</span>
                    {form.projects.length > 1 && <button className="re-del-btn" onClick={() => delArr('projects', i)}>Remove</button>}
                  </div>
                  <Field label="Project Name" value={proj.name}      placeholder="Resume Builder App"        onChange={v => setArr('projects', i, 'name', v)} />
                  <Field label="Tech Stack"   value={proj.techStack}  placeholder="React, Node.js, MongoDB"  onChange={v => setArr('projects', i, 'techStack', v)} />
                  <Field type="textarea" label="Description" rows={3} value={proj.description} placeholder="What did this project do and what did you achieve?" onChange={v => setArr('projects', i, 'description', v)} />
                </div>
              ))}
              <button className="re-add-btn" style={{ color: accent }}
                onClick={() => addArr('projects', { name: '', techStack: '', description: '' })}>
                + Add Project
              </button>
            </Section>

            {/* ── Skills ── */}
            <div ref={el => sectionRefs.current.skills = el} className="re-section-anchor" />
            <Section id="skills" title="Skills" visible={activeSection === 'skills'}
              onNext={goNextSection} onPrev={goPrevSection} nextLabel="Next"
              expanded={open.skills} onToggle={id => { setActiveSection(id); toggle(id); }} accent={accent}>
              <div className="re-skill-input-row">
                <input className="re-input" value={skillInput} placeholder="e.g. React, Python, SQL"
                  onChange={e => setSkillInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addSkill()} />
                <button className="re-add-skill-btn" style={{ background: accent }} onClick={addSkill}>Add</button>
              </div>
              <div className="re-skills-chips">
                <AnimatePresence>
                  {form.skills.technical.map((s, i) => (
                    <motion.span key={s + i} className="re-chip" style={{ borderColor: `${accent}55`, color: accent }}
                      initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }}>
                      {s} <button onClick={() => delSkill(i)}>×</button>
                    </motion.span>
                  ))}
                </AnimatePresence>
              </div>
            </Section>

            {/* ── Positions ── */}
            <Section id="positions" title="Positions of Responsibility" visible={activeSection === 'positions'}
              onNext={goNextSection} onPrev={goPrevSection} nextLabel="Next"
              expanded={open.positions} onToggle={id => { setActiveSection(id); toggle(id); }} accent={accent}>
              {form.positions.map((pos, i) => (
                <div key={i} className="re-array-item">
                  <div className="re-array-head">
                    <span>Position {i + 1}</span>
                    {form.positions.length > 1 && <button className="re-del-btn" onClick={() => delArr('positions', i)}>Remove</button>}
                  </div>
                  <Field label="Title"        value={pos.title}        placeholder="Team Leader"        onChange={v => setArr('positions', i, 'title', v)} />
                  <Field label="Organisation" value={pos.organization} placeholder="Organisation Name"  onChange={v => setArr('positions', i, 'organization', v)} />
                  <Field label="Duration"     value={pos.duration}     placeholder="2022 – Present"     onChange={v => setArr('positions', i, 'duration', v)} />
                </div>
              ))}
              <button className="re-add-btn" style={{ color: accent }}
                onClick={() => addArr('positions', { title: '', organization: '', duration: '' })}>
                + Add Position
              </button>
            </Section>

            {/* ── Achievements ── */}
            <Section id="achievements" title="Achievements" visible={activeSection === 'achievements'}
              onNext={goNextSection} onPrev={goPrevSection} nextLabel="Next"
              expanded={open.achievements} onToggle={id => { setActiveSection(id); toggle(id); }} accent={accent}>
              {form.achievements.map((ach, i) => (
                <div key={i} className="re-array-item">
                  <div className="re-array-head">
                    <span>Achievement {i + 1}</span>
                    <button className="re-del-btn" onClick={() => delArr('achievements', i)}>Remove</button>
                  </div>
                  <Field type="textarea" rows={2} value={ach} placeholder="First place in Hackathon 2024"
                    onChange={v => setArr('achievements', i, null, v)} />
                </div>
              ))}
              <button className="re-add-btn" style={{ color: accent }}
                onClick={() => addArr('achievements', '')}>
                + Add Achievement
              </button>
            </Section>

            {/* ── Certifications ── */}
            <Section id="certifications" title="Certifications" visible={activeSection === 'certifications'}
              onNext={goNextSection} onPrev={goPrevSection} nextLabel="Next"
              expanded={open.certifications} onToggle={id => { setActiveSection(id); toggle(id); }} accent={accent}>
              {form.certifications.map((cert, i) => (
                <div key={i} className="re-array-item">
                  <div className="re-array-head">
                    <span>Cert {i + 1}</span>
                    {form.certifications.length > 1 && <button className="re-del-btn" onClick={() => delArr('certifications', i)}>Remove</button>}
                  </div>
                  <Field label="Certificate Name" value={cert.name}   placeholder="Azure AI Fundamentals" onChange={v => setArr('certifications', i, 'name', v)} />
                  <Row>
                    <Field label="Issuer" value={cert.issuer} placeholder="Microsoft"  onChange={v => setArr('certifications', i, 'issuer', v)} />
                    <Field label="Date"   value={cert.date}   placeholder="Dec 2023"   onChange={v => setArr('certifications', i, 'date', v)} />
                  </Row>
                </div>
              ))}
              <button className="re-add-btn" style={{ color: accent }}
                onClick={() => addArr('certifications', { name: '', issuer: '', date: '' })}>
                + Add Certification
              </button>
            </Section>

            {/* ── Other ── */}
            <div ref={el => sectionRefs.current.other = el} className="re-section-anchor" />
            <Section id="other" title="Other Sections" visible={activeSection === 'other'}
              onNext={() => download('pdf')} onPrev={goPrevSection} nextLabel="Download PDF"
              expanded={open.other} onToggle={id => { setActiveSection(id); toggle(id); }} accent={accent}>
              {form.other.map((item, i) => (
                <div key={i} className="re-array-item">
                  <div className="re-array-head">
                    <span>{item.heading || `Section ${i + 1}`}</span>
                    <button className="re-del-btn" onClick={() => delArr('other', i)}>Remove</button>
                  </div>
                  <Field label="Heading" value={item.heading} placeholder="Languages, Hobbies, etc." onChange={v => setArr('other', i, 'heading', v)} />
                  <Field type="textarea" label="Content" rows={3} value={item.content} placeholder="Add details" onChange={v => setArr('other', i, 'content', v)} />
                </div>
              ))}
              <button className="re-add-btn" style={{ color: accent }}
                onClick={() => addArr('other', { heading: '', content: '', placement: 'main' })}>
                + Add Section
              </button>
            </Section>
          </div>
        </div>

        {/* ── Right Preview ── */}
        <div className="re-preview-col">
          <div className="re-preview-wrapper">
            <div className="re-preview-status" style={{ borderColor: accent }}>
              <span className="re-preview-status-dot" style={{ background: accent }} />
              <span>Live preview enabled</span>
            </div>
            <div className="re-preview-topbar">
              <span>Live Preview</span>
              <span className="re-preview-tpl-tag" style={{ color: accent, borderColor: `${accent}44` }}>
                {(templateId || 'default').toUpperCase()}
              </span>
            </div>
            <div className="re-preview-scroll">
              {/* ── Real template PDF preview ── */}
              <LivePreview data={form} resumeId={resumeId} token={token} />
            </div>

            {/* ── Download buttons ── */}
            <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: 10, flexShrink: 0 }}>
              <button onClick={() => download('pdf')} disabled={downloading}
                style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', background: accent, color: '#fff', fontWeight: 700, fontSize: 13, cursor: downloading ? 'not-allowed' : 'pointer', opacity: downloading ? 0.7 : 1 }}>
                {downloading ? 'Generating…' : 'Download PDF'}
              </button>
              <button onClick={() => download('ats-docx')} disabled={downloading}
                style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: `1.5px solid ${accent}`, background: 'transparent', color: accent, fontWeight: 700, fontSize: 13, cursor: downloading ? 'not-allowed' : 'pointer', opacity: downloading ? 0.7 : 1 }}>
                {downloading ? 'Generating…' : 'ATS Word'}
              </button>
            </div>
            <div className="re-preview-footer">
              <a href="/change-template">Change template</a>
              <p>PDF = beautiful design. ATS Word = plain text for job portals.</p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ResumeEditor;